import Axios from "../config/axios-v1-config";
import { apiErrorCode, apiErrorMessage, apiErrorStatus } from "../utils/apiErrorMessage";

// Uniform error for the wrappers below: `message` is backend text safe to show
// or null (network error, empty body, generic phrase) so the caller falls back
// to its own translated text. Never an untranslated literal nor the raw body.
const toChannelError = (err) => ({
  error: {
    message: apiErrorMessage(err, null),
    status: apiErrorStatus(err),
    code: apiErrorCode(err),
  },
});

export const getChannelIntegrations = async (params = {}) => {
  try {
    const response = await Axios.get("channel-integrations", { params });
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

export const connectMetaChannel = async ({ code, channel_hint, redirect_uri }) => {
  try {
    const response = await Axios.post("channel-integrations/meta/callback", {
      code,
      channel_hint,
      redirect_uri,
    });
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

export const disconnectChannel = async (integrationId) => {
  try {
    const response = await Axios.patch(
      `channel-integrations/${integrationId}/disconnect`
    );
    return response.data;
  } catch (err) {
    // No `response` (network/CORS) means no body: return {message:null} so the
    // caller falls back to its translated text instead of an untranslated literal.
    return { error: err.response?.data ?? { message: null } };
  }
};

export const removeChannel = async (integrationId) => {
  try {
    const response = await Axios.delete(`channel-integrations/${integrationId}`);
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

export const testChannelIntegration = async (integrationId) => {
  try {
    const response = await Axios.post(`channel-integrations/${integrationId}/test`);
    return response.data;
  } catch (err) {
    // Same as disconnectChannel: a network error carries no body.
    return { error: err.response?.data ?? { message: null } };
  }
};

// ── Unofficial WhatsApp Instances ──────────────────────────────────

export const createUnofficialWaInstance = async (displayName, groupId, purpose) => {
  try {
    const response = await Axios.post("channel-integrations/unofficial-wa/instances", {
      display_name: displayName,
      group_id: groupId || undefined,
      purpose: purpose || undefined,
    });
    return response.data;
  } catch (err) {
    // QrCodeModal shows `error` only when it is a string: keep that contract,
    // but null (not a literal) when the backend sent nothing showable.
    return { error: apiErrorMessage(err, null) };
  }
};

// Fija el propósito de un número no oficial: "notifications" | "helpdesk" | "both".
export const setInstancePurpose = async (integrationId, purpose) => {
  try {
    const response = await Axios.patch(
      `channel-integrations/unofficial-wa/instances/${integrationId}/purpose`,
      { purpose }
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

export const disconnectUnofficialWa = async (integrationId) => {
  try {
    const response = await Axios.delete(
      `channel-integrations/unofficial-wa/instances/${integrationId}`
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

// Eliminación permanente (hard delete) de un número desconectado.
export const deleteUnofficialWaPermanent = async (integrationId) => {
  try {
    const response = await Axios.delete(
      `channel-integrations/unofficial-wa/instances/${integrationId}/permanent`
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

// Qué implica retirar o migrar un número: registros del mismo teléfono,
// conversaciones (totales/abiertas), si tiene grupo de avisos y los números
// conectados que pueden reemplazarlo.
export const getUnofficialWaRetirementPreview = async (integrationId) => {
  try {
    const response = await Axios.get(
      `channel-integrations/unofficial-wa/instances/${integrationId}/retirement-preview`
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

// Retiro definitivo del número (idempotente). mode "migrate" con
// replacementIntegrationId: todo pasa al número nuevo. mode "retire": se retira
// sin reemplazo y sus conversaciones abiertas salen de las colas.
export const retireUnofficialWa = async (integrationId, { mode, replacementIntegrationId } = {}) => {
  try {
    const response = await Axios.post(
      `channel-integrations/unofficial-wa/instances/${integrationId}/retire`,
      {
        mode,
        ...(mode === "migrate" ? { replacement_integration_id: replacementIntegrationId } : {}),
      }
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

export const restartUnofficialWa = async (integrationId) => {
  try {
    const response = await Axios.post(
      `channel-integrations/unofficial-wa/instances/${integrationId}/restart`
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

export const getUnofficialWaQr = async (integrationId) => {
  try {
    const response = await Axios.get(
      `channel-integrations/unofficial-wa/instances/${integrationId}/qr`
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

export const getUnofficialWaStatus = async (integrationId) => {
  try {
    const response = await Axios.get(
      `channel-integrations/unofficial-wa/instances/${integrationId}/status`
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

// ── Business Groups ────────────────────────────────────────────────

export const createWaBusinessGroup = async (name, description, externalCommerceId) => {
  try {
    const response = await Axios.post("channel-integrations/unofficial-wa/groups", {
      name,
      description,
      external_commerce_id: externalCommerceId || undefined,
    });
    return response.data;
  } catch (err) {
    // Sin `response` (red caída, CORS) no hay body que mostrar: {message:null}
    // deja que el llamador caiga a su fallback traducido. Un literal como
    // "Error desconocido" pasaría el filtro de apiBodyMessage y se pintaría
    // sin traducir.
    return { error: err.response?.data ?? { message: null } };
  }
};

export const getWaBusinessGroups = async () => {
  try {
    const response = await Axios.get("channel-integrations/unofficial-wa/groups");
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

export const updateWaBusinessGroup = async (groupId, data) => {
  try {
    const response = await Axios.patch(
      `channel-integrations/unofficial-wa/groups/${groupId}`,
      data
    );
    return response.data;
  } catch (err) {
    // Mismo criterio que createWaBusinessGroup: sin body no se inventa texto.
    return { error: err.response?.data ?? { message: null } };
  }
};

export const deleteWaBusinessGroup = async (groupId) => {
  try {
    const response = await Axios.delete(
      `channel-integrations/unofficial-wa/groups/${groupId}`
    );
    return response.data;
  } catch (err) {
    return { error: err.response?.data ?? { message: null } };
  }
};

export const assignInstanceToGroup = async (integrationId, groupId) => {
  try {
    const response = await Axios.patch(
      `channel-integrations/unofficial-wa/instances/${integrationId}/group`,
      { group_id: groupId }
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

// ── Grupo de notificaciones operativas (grupo WhatsApp real "@g.us") ──

export const getInstanceWhatsappGroups = async (integrationId) => {
  try {
    const response = await Axios.get(
      `channel-integrations/unofficial-wa/instances/${integrationId}/whatsapp-groups`
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};

export const setOpsNotificationGroup = async (integrationId, groupJid, groupName) => {
  try {
    const response = await Axios.patch(
      `channel-integrations/unofficial-wa/instances/${integrationId}/ops-group`,
      { group_jid: groupJid || "", group_name: groupName || "" }
    );
    return response.data;
  } catch (err) {
    return toChannelError(err);
  }
};
